import React from "react";
import { Dropdown, makeStyles } from "@seahorse/ui";
import {
  DisplayTimeZoneSetting,
  getDisplayTimeZoneOptions,
  setDisplayTimeZoneSetting,
  useDisplayTimeZone,
} from "../../Utils/displayTimeZone";

interface DisplayTimeZoneDropdownProps {
  /** Active pair; its two currencies' markets are offered after UTC and Local. */
  currencyPair?: string | null;
  titleClassName?: string;
  containerClassName?: string;
}

// Market labels ("South Africa (Johannesburg, SAST)") can be wider than the
// popover. The select otherwise sizes to its nowrap label, so pin every level
// to the container width and ellipsise the text instead.
const useStyles = makeStyles({
  fitContainer: {
    width: "100%",
    minWidth: 0,
    "& .MuiFormControl-root, & .MuiInputBase-root": {
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
    },
    "& .MuiSelect-select": {
      minWidth: 0,
      overflow: "hidden",
    },
    "& .MuiSelect-select > *": {
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
});

/**
 * Popover control for the global display time zone. The change applies
 * immediately to every mounted CoTrader surface.
 */
const DisplayTimeZoneDropdown: React.FunctionComponent<
  DisplayTimeZoneDropdownProps
> = ({ currencyPair, titleClassName, containerClassName }) => {
  const classes = useStyles();
  const { setting } = useDisplayTimeZone();
  // Rebuilt on render so zone labels reflect the current DST state.
  const items = getDisplayTimeZoneOptions(currencyPair, setting);
  const selectedLabel = items.find((item) => item.value === setting)?.label;

  return (
    <>
      <div className={titleClassName}>Time Zone</div>
      <div
        className={[containerClassName, classes.fitContainer]
          .filter(Boolean)
          .join(" ")}
        // Full label on hover when it is truncated.
        title={selectedLabel}
      >
        <Dropdown<DisplayTimeZoneSetting>
          // Remount when the option set changes: the Dropdown keeps its own
          // copy of the value, which would otherwise briefly point at an
          // option that was just removed and leave the menu unresponsive.
          key={items.map((item) => item.value).join("|")}
          items={items}
          value={setting}
          onChange={(value) => setDisplayTimeZoneSetting(value)}
          placeholder="Select time zone"
          fullWidth={true}
        />
      </div>
    </>
  );
};

export default DisplayTimeZoneDropdown;
